import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_POLLING_TIMEOUT = 30000; // Thirty seconds
const DEFAULT_POLLING_INTERVAL = 1000; // One second

type UsePollingOptions<T> = {
  /** Async callback called on each poll interval, returned value passed to predicate */
  action: () => Promise<T>;
  /** Returns true when polling should stop */
  predicate: (data: T) => boolean;
  /** Called when condition is met or timeout expires */
  onComplete?: () => void;
  /** Max time before giving up and calling onComplete (default: 30000ms) */
  timeout?: number;
  /** Time between polls (default: 1000ms) */
  interval?: number;
};

type UsePollingReturn = {
  startPolling: () => void;
};

/**
 * Once polling is started it will call the action callback every interval
 * until the predicate returns true or the timeout is reached.
 */
export function usePolling<TData>({
  action,
  predicate,
  onComplete = () => {},
  timeout = DEFAULT_POLLING_TIMEOUT,
  interval = DEFAULT_POLLING_INTERVAL,
}: UsePollingOptions<TData>): UsePollingReturn {
  const [isPolling, setIsPolling] = useState(false);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const actionRef = useRef(action);
  const predicateRef = useRef(predicate);
  const onCompleteRef = useRef(onComplete);
  actionRef.current = action;
  predicateRef.current = predicate;
  onCompleteRef.current = onComplete;

  const cleanup = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const startPolling = useCallback(() => {
    if (isPolling) return;
    setIsPolling(true);
    intervalIdRef.current = setInterval(async () => {
      const data = await actionRef.current();
      if (predicateRef.current(data)) {
        cleanup();
        onCompleteRef.current();
      }
    }, interval);
    timeoutIdRef.current = setTimeout(() => {
      cleanup();
      onCompleteRef.current();
    }, timeout);
  }, [isPolling, interval, timeout, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  return {
    startPolling,
  };
}
